import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fonts, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../Styles';
import { NewButton, NewButtonList, NewInput, NewSelect, Overlay, TableButton } from '..';
import { ListData_ValueTypes, TableStyles_Types, ButtonTypes, SelectFieldOption_Values, NewFieldStyles, NewFieldTypes, ValueSteps, MenuFloats, NewInputHandles, NewSelectHandles } from '../../Definitions';
import { cloneDeep, isArray } from 'lodash';
import { useFeedcurveOptions } from '../_Components';
import { createPortal } from 'react-dom';

// Table
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getSortedRowModel, Row, useReactTable } from '@tanstack/react-table';
import { ReactiveTable } from '../Tables/reactiveTableRenderer';
import { updateFeedCurveTable } from './_FeedCurves/updateFeedCurveTableHelper';
import { GenericDataParameters, Units, useColumnConstructor } from '../Tables/_Tables/useColumnConstructor';
import { convertFeedCurveToTableData, FeedCurveTableData, handleNewFeedComponent, handleEnergyEdit, handlePercentileEdit, handleRemoveComponent, SHOULD_DISPLAY_CONTINUES, handleInterpolatedFeedRow } from './_FeedCurves/feedCurveTableUtils';
import { FeedCurve, FeedCurveComponent, FeedCurveMilestone } from '../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall';
import { DynamicDataObk, FeedComponentInfo, MaintainFeedCurve, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveUpdates } from '../../Services';
import { useFeedCurveContext } from './_FeedCurves/feedCurveContext';
import { styled } from 'styletron-react';

/* eslint-disable */
enum Unit {
  kg = 1,
  percentile = 2,
  energy = 3,
}
/* eslint-enable */

// #region | Feed Skip
function useSkipper() {
  const shouldSkipRef = useRef(true)
  const shouldSkip = shouldSkipRef.current

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = useCallback(() => {
    shouldSkipRef.current = false
  }, [])

  useEffect(() => {
    shouldSkipRef.current = true
  })

  return [shouldSkip, skip] as const
}
// #endregion

interface FeedCurveTableProps {
  setDisableCurveNavigation: Dispatch<SetStateAction<boolean>>;
  milestoneOptions: SelectFieldOption_Values[];
  watchFeedCurve?: () => void;
  feedCurve: FeedCurve;
  maintainFeedCurve: (changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurve, optimisticFeedCurve: FeedCurve}) => Promise<boolean | void | [boolean, string]>;
  feedComponents: FeedComponentInfo[] | undefined
  allFeedComponents: FeedComponentInfo[] | undefined
  initialDay?: number;
  initialMilestone?: FeedCurveMilestone
}

const BASE_FEED_CURVE_KEY = "baseFeedCurve" as const;
/**
 * 
 */
export const FeedCurveTable = ({ initialDay, initialMilestone, setDisableCurveNavigation, watchFeedCurve, feedCurve: originalFeedCurve, milestoneOptions, maintainFeedCurve, feedComponents, allFeedComponents }: FeedCurveTableProps) => {
  // #region | Hooks, options, refs, states, mediaquries
  // Hooks
  const { t } = useTranslation();

  // Options
  const { milestoneOptionsFromFeedcurve } = useFeedcurveOptions(undefined, originalFeedCurve.feedCurveId);

  // Refs
  const milestoneDayRef = useRef<NewInputHandles | null>(null);
  const milestoneRef = useRef<NewSelectHandles | null>(null);
  const createdMilestoneIdRef = useRef<number>(1);
  const createdDayComponentsIdRef = useRef<number>(1);

  const [loading, setLoading] = useState(false);
  const [errorMilestones, setErrorMilestones] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string>();
  // States
  const [unitMode, setUnitMode] = useState(Unit.kg);
  const [editTable, setEditTable] = useState(false);
  const [feedCurve, setFeedCurve] = useState(cloneDeep(originalFeedCurve));
  const [addedComponent, setAddedComponent] = useState<number|undefined>();
  const [removedComponent, setRemovedComponent] = useState<{componentId: number, feedCurveComponentId: number, componentIndex:number}|undefined>();
  const [changedTableData, setChangedTableData] = useState<{ rowId: number, columnId: string, value: string | number | boolean | SelectFieldOption_Values | undefined, row: Row<FeedCurveTableData> } | undefined>();
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  
  // Mediaquries
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);
  const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
  // #endregion
 
  // #region | GraphQl
  const data = useMemo(() => {return convertFeedCurveToTableData(feedCurve, setFeedCurve, createdDayComponentsIdRef, createdMilestoneIdRef, SHOULD_DISPLAY_CONTINUES)}, [feedCurve])
  const usedComponentIds = useMemo(() => feedCurve.components?.map((component) => component.componentId),[feedCurve.components]);
  const dirtRef = useRef<{[key: number]: MaintainFeedCurve} & {[BASE_FEED_CURVE_KEY]: MaintainFeedCurve}>({baseFeedCurve: { feedCurveId: feedCurve.feedCurveId }})
  // #endregion

  const { state, dispatch } = useFeedCurveContext()

  const selectedMilestone = state.milestone && state.milestone.selectedMilestone
  const selectedDay = state.milestone && state.milestone.selectedDay

  useEffect(() => {
    setFeedCurve(cloneDeep(originalFeedCurve));
  }, [originalFeedCurve])

  useEffect(() => {
   if (addedComponent) {
    setAddedComponent(undefined);
    setFeedCurve(cloneDeep(handleNewFeedComponent({feedComponentId: addedComponent, feedComponents, dirtRef, feedCurve})))
   } 
  },[addedComponent, feedComponents, feedCurve])

  useEffect(() => {
    if (removedComponent) {
      setRemovedComponent(undefined);
      setFeedCurve(handleRemoveComponent({...removedComponent, feedCurve, dirtRef}))
    }
  },[feedCurve, removedComponent])

  useEffect(() => {
   if (changedTableData) {
    setChangedTableData(undefined);
    updateFeedCurveTable({...changedTableData, feedCurve, createdDayComponentsIdRef, createdMilestoneIdRef, data, setFeedCurve, dirtRef, skipAutoResetPageIndex, energyMode: unitMode === Unit.energy})
   } 
  }, [changedTableData, data, feedCurve, skipAutoResetPageIndex, unitMode])
  
  useEffect(() => {
      milestoneDayRef.current?.setValue(selectedDay ?? "");
      milestoneRef.current?.setValue(milestoneOptionsFromFeedcurve?.find(m => m.id === selectedMilestone?.feedCurveMilestoneId) ?? undefined);
  }, [initialDay, initialMilestone, milestoneOptionsFromFeedcurve, selectedMilestone?.feedCurveMilestoneId, selectedDay, state.milestone?.selectedDay, state.milestone?.selectedMilestone]);

  // #region | Unit handler
  function getUnit() {
    switch (unitMode) {
      case Unit.kg: {
        return Units.kg;
      }
      case Unit.percentile: {
        return Units.percent;
      }
      case Unit.energy: {
        return Units.energy;
      }
      default: {
        return Units.kg;
      }
    }
  }
  // #endregion

  // #region | Table
  const tableData = useMemo(() => {
    let baseData =
      unitMode === Unit.energy
        ? Array.from(handleEnergyEdit(data, feedCurve.components, editTable).values())
        : Array.from(handlePercentileEdit(data, usedComponentIds, unitMode === Unit.percentile).values());

    if (selectedDay !== undefined && selectedMilestone !== undefined) {
      const milestoneRows = baseData.filter(row => row.milestoneId === selectedMilestone.feedCurveMilestoneId);

      const infimum = milestoneRows.filter(d => d.day <= selectedDay).sort((a, b) => b.day - a.day).at(0);
      const supremum = milestoneRows.filter(d => d.day >= selectedDay).sort((a, b) => a.day - b.day).at(0);
      const dayExists = milestoneRows.some(row => row.day === selectedDay);

      if (!dayExists && infimum && supremum && infimum.day !== supremum.day) {
        const newRow = handleInterpolatedFeedRow(
          feedCurve.components,
          infimum,
          supremum,
          selectedDay
        );

        if (newRow) {
          baseData = [
            ...baseData,
            newRow
          ].sort((a, b) => {
            if (a.milestoneArrayIndex !== b.milestoneArrayIndex) {
              return a.milestoneArrayIndex - b.milestoneArrayIndex;
            }
            return a.day - b.day;
          });
        }
      }
    }

    baseData.sort((a, b) => {
      if (a.milestoneArrayIndex !== b.milestoneArrayIndex) {
        return a.milestoneArrayIndex - b.milestoneArrayIndex;
      }
      return a.day - b.day;
    });

    return baseData;
  }, [data, feedCurve.components, editTable, unitMode, usedComponentIds, selectedDay, selectedMilestone]);

  // Max Select Day
  const selectedMilestoneDays = feedCurve.milestones?.find(m => m.feedCurveMilestoneId === selectedMilestone?.feedCurveMilestoneId)?.feedDays
  const maxDay = selectedMilestoneDays && Math.max(...selectedMilestoneDays.map(d => d.day))

  // Accessor data
  const accessorData = useMemo<GenericDataParameters<FeedCurveTableData>[]>(() => ([
    {headerValue: t("feedCurvePages.feedCurve.day", {defaultValue: "day"}), accessor: "day", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers},
    {headerValue: t("feedCurvePages.components.ds", {defaultValue: "ds"}), accessor: "ds", type: ListData_ValueTypes.Number, staticColumn: true, unit: Units.percent},
    {headerValue: t("units.total", {defaultValue: "Total"}), accessor: "totalAmount", type: ListData_ValueTypes.Number, staticColumn: unitMode === Unit.kg ? true : unitMode === Unit.energy, unit: Units.kg},
    {headerValue: t("milestones.general.milestone", {defaultValue: "milestone"}), accessor: "milestone", type: ListData_ValueTypes.List, options: milestoneOptions},
    {headerValue: "", accessor: "addDay", type: ListData_ValueTypes.Button, width: "42px"},
    {headerValue: "notApplicable", accessor: "removeRowActionColumn", type: ListData_ValueTypes.DeleteRowAction, width: "42px", },
    ...(feedCurve.components ?? []).map(({ name, componentId, feedCurveComponentId }, index) => ({
      accessor: String(componentId),
      staticColumn: unitMode === Unit.percentile,
      unit: getUnit(),
      idForColor: componentId,
      type: ListData_ValueTypes.Number,
      valueStep: ValueSteps.floats,
      width: "minmax(150px, 1fr)",
      headerValue: name,
      onClick: (editTable ? () => { setRemovedComponent({ componentId, feedCurveComponentId, componentIndex: index }) } : undefined),
      forceRerenderOnTableEdit:  editTable ? undefined : true as true,
      onHoverContent: (editTable ? undefined :
        <ThisComponent feedComponent={feedCurve.components?.find(f => f.componentId === componentId)} dbComponent={allFeedComponents?.find((component) => component.id === componentId)} />)
    })),
    {
      headerValue: !editTable ? "" :
      <NewSelect
        onChange={(value) => {setAddedComponent(Number(value?.id))}}
        options = {feedComponents?.map((component) => ({label: component.name, id: component.id})) ?? []}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.transparent,
          fieldType: NewFieldTypes.tableHeaderCell,
          fieldWidth: "25px",
          menuWidth: "150px",
          menuFloat: MenuFloats.center
        }}
        istableHeaderCell
      />,
      accessor: "nonValidAccessorValueAddComponent",
      type: ListData_ValueTypes.Display,
      sort: false,
      width: feedCurve.components?.length === 0 ? undefined : "70px"
    }
  ]), [t, unitMode, milestoneOptions, feedCurve.components, editTable, feedComponents, allFeedComponents])
  // Table construction
  const columns = useColumnConstructor<FeedCurveTableData>({accessorData: [...accessorData], tableStyle: TableStyles_Types.primaryTable, canEdit: editTable})
  const table = useReactTable<FeedCurveTableData>({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    autoResetPageIndex,
    state: {
      columnFilters,
      columnOrder: ["milestone","day", "addDay", "removeRowActionColumn", ...(feedCurve.components??[]).map((component)=>(String(component.componentId))), "nonValidAccessorValueAddComponent", "totalAmount", "ds"],
      columnPinning: {
        left: mobileXs? undefined : ["milestone", "day", "addDay", "removeRowActionColumn"],
        right: mobileXs? undefined : ["totalAmount", "ds"],
      },
    },
    meta: {
      // #region Save function
      updateData: (rowId, columnId, value, row) => {
        setChangedTableData({rowId, columnId, value, row})
      },
      // #endregion
      // #region Delete function
      deleteRow: (rowId) => {
        const oldRow = data.get(rowId);
        let feedPlanEntriesToDelete: number[] = []; 
        if (feedCurve.milestones && oldRow) {
          const feedCurveMilestone = feedCurve.milestones[oldRow.milestoneArrayIndex]
          if (feedCurveMilestone) {
            feedCurveMilestone.feedDays.splice(oldRow.dayIndex, 1)
          }
          if (feedCurveMilestone.feedDays?.length === 0) {

            feedPlanEntriesToDelete = feedCurve.milestones[oldRow.milestoneArrayIndex]?.feedPlan?.plan.map((planEntry) => planEntry.feedPlanEntryId) ?? []
            feedCurve.milestones.splice(oldRow.milestoneArrayIndex, 1)
          }
        }
        setFeedCurve(cloneDeep(feedCurve));
        if (oldRow === undefined) {
          return;
        } 
        const feedCurveMilestoneId = oldRow.milestoneId;
        if (dirtRef.current[feedCurveMilestoneId] === undefined) {
          dirtRef.current[feedCurveMilestoneId] = {feedCurveId: feedCurve.feedCurveId}
        }
        if (dirtRef.current[feedCurveMilestoneId].deletes === undefined) {
          dirtRef.current[feedCurveMilestoneId].deletes = {};
        }
        if (dirtRef.current[feedCurveMilestoneId].deletes.feedDays === undefined) {
          dirtRef.current[feedCurveMilestoneId].deletes.feedDays = [];
        }
        if (rowId < 0) {
          if (dirtRef.current[feedCurveMilestoneId].creates?.milestones) {
            const milestoneArrayIndex = dirtRef.current[feedCurveMilestoneId].creates.milestones.findIndex((milestone) => (milestone.temporaryId === oldRow.feedCurveMilestoneId));
            if (milestoneArrayIndex === -1 ) {
              return;
            }
            dirtRef.current[feedCurveMilestoneId].creates.milestones[milestoneArrayIndex].feedDays = dirtRef.current[feedCurveMilestoneId].creates.milestones[milestoneArrayIndex].feedDays?.filter((feedDay) => (feedDay.temporaryId !== rowId));
            if (dirtRef.current[feedCurveMilestoneId].creates.milestones[milestoneArrayIndex].feedDays?.length === 0) {
              dirtRef.current[feedCurveMilestoneId].creates.milestones.splice(milestoneArrayIndex, 1);
            }
          }
          return
        }
        if (oldRow.milestone && oldRow.removeRowActionColumn === "visible") {
          if (dirtRef.current[feedCurveMilestoneId].deletes.milestones === undefined) {
            dirtRef.current[feedCurveMilestoneId].deletes.milestones = [];
          }
          if (dirtRef.current[feedCurveMilestoneId].deletes.feedPlan === undefined) {
            dirtRef.current[feedCurveMilestoneId].deletes.feedPlan = [];
          }
          if (feedPlanEntriesToDelete.length) {
            dirtRef.current[feedCurveMilestoneId].deletes.feedPlan.push(...feedPlanEntriesToDelete)
          }
          dirtRef.current[feedCurveMilestoneId].deletes.milestones.push(oldRow.milestoneId) // milestoneId is feedCurveMilestoneId
        }
        dirtRef.current[feedCurveMilestoneId].deletes.feedDays.push(rowId)
      },
      // #endregion
      allowDeleteRowCheck: (row) => {return (row.original.removeRowActionColumn === "visible")}
    }
  })

  const highlightedRowId = useMemo(() => {
    if (selectedMilestone === undefined || selectedDay === undefined) return undefined;
  
    const match = table
      .getRowModel()
      .rows.find(
        (row) =>
          row.original.milestoneId === selectedMilestone.feedCurveMilestoneId &&
          row.original.day === selectedDay
      );
  
    return match ? Number(match.original.id ?? match.id) : undefined;
  }, [selectedMilestone, selectedDay, table]);
  // #endregion

  // #region | Table Btn functions
  const clearDirt = () => {
    dirtRef.current = {baseFeedCurve: {feedCurveId: feedCurve.feedCurveId}};
    setFeedCurve(cloneDeep(originalFeedCurve));
  }
  
  const switchTable = () => {
    dispatch({milestone: {selectedMilestone: undefined, selectedDay: undefined}})

    setDisableCurveNavigation(!editTable);
    setEditTable((editTable) => !editTable);
  };

  const saveChanges = async () => {
    setDisableCurveNavigation(!editTable);
    setErrorMilestones([])
    const changes: [string, MaintainFeedCurve][] = [[BASE_FEED_CURVE_KEY, dirtRef.current.baseFeedCurve], ...Array.from(Object.entries(dirtRef.current)).reduce((accumulator, [key, value]) => {if (key !== "baseFeedCurve") {accumulator.push([key, value])} return accumulator}, [] as [string, MaintainFeedCurve][])]
    
    setLoading(true);
    const errors: string[] = [];
    for (const [key, changeSet] of changes) {
      const result = await maintainFeedCurve(
        { data: changeSet.updates },
        { data: changeSet.deletes },
        { data: changeSet.creates },
        { originalFeedCurve, optimisticFeedCurve: feedCurve }
      )
      if (isArray(result) && result[0] === false) {
        if (key === BASE_FEED_CURVE_KEY) {
          setErrorMessage("Something went wrong when attempting to add or remove a component");
        }
        if (Number(key) > 0 && changeSet.deletes && changeSet.deletes.milestones?.length && feedCurve.info?.active) {
          setErrorMessage("Can't delete an active feed curve milestone. Remove animals from feed curve milestones before deletion")
        }
        if (Number(key) > 0) {
          const milestoneLabel = milestoneOptionsFromFeedcurve?.find(({id}) => id === Number(key))?.label;
          if (milestoneLabel) {
            errors.push(milestoneLabel);
          }
        }
        if (Number(key) < 0 && changeSet.creates?.milestones && changeSet.creates?.milestones?.at(0)?.milestone_id) {
          const milestoneLabel = milestoneOptions.find(({id}) => id === changeSet.creates!.milestones!.at(0)?.milestone_id)?.label;         
          if (milestoneLabel) {
           errors.push(milestoneLabel) 
          }
        }
      }
    } 
    if (errors.length) {
      setErrorMilestones(errors)
    }
    setLoading(false);
    dirtRef.current = {"baseFeedCurve": { feedCurveId: feedCurve.feedCurveId }};
  };
  // #endregion

  // #region | Select milestone and milestone day
  const changeDay = (direction: "up" | "down") => {
    if (selectedMilestone === undefined || selectedDay === undefined || !milestoneOptionsFromFeedcurve) return;
  
    const currentMilestoneIndex = milestoneOptionsFromFeedcurve.findIndex(
      (m) => m.id === selectedMilestone.feedCurveMilestoneId
    );
  
    const currentMilestoneData = feedCurve.milestones?.find(
      (m) => m.feedCurveMilestoneId === selectedMilestone.feedCurveMilestoneId
    );
    if (!currentMilestoneData) return;
  
    const maxDayForCurrent = Math.max(...currentMilestoneData.feedDays.map((d) => d.day));
  
    if (direction === "up") {
      if (selectedDay < maxDayForCurrent) {
        const newDay = selectedDay + 1;
        dispatch({milestone: {selectedMilestone: selectedMilestone, selectedDay: newDay}})
      } else {
        const nextMilestone = milestoneOptionsFromFeedcurve[currentMilestoneIndex + 1];
        if (!nextMilestone) return;

        const nextMilestoneData = feedCurve.milestones?.find(
          (m) => m.feedCurveMilestoneId === nextMilestone.id
        );
        if (nextMilestone) {
          dispatch({ milestone: { selectedMilestone: nextMilestoneData, selectedDay: 0 } })
        }
      }
    } else {
      if (selectedDay > 0) {
        const newDay = selectedDay - 1;
        dispatch({milestone: { selectedMilestone: selectedMilestone, selectedDay: newDay}})
      } else if (currentMilestoneIndex > 0) {
        const prevMilestone = milestoneOptionsFromFeedcurve[currentMilestoneIndex - 1];
        const prevMilestoneData = feedCurve.milestones?.find(
          (m) => m.feedCurveMilestoneId === prevMilestone.id
        );
  
        if (prevMilestoneData) {
          const maxDayForPrev = Math.max(...prevMilestoneData.feedDays.map(d => d.day));
          dispatch({ milestone: { selectedMilestone: prevMilestoneData, selectedDay: maxDayForPrev } })
        }
      }
    }
  };
  
  const milestoneSelect = (
    <div style={{display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%", height: "30px", gap: "5px", marginBottom: "5px"}}>
      <NewSelect
        isClearable
        ref={milestoneRef}
        options={milestoneOptionsFromFeedcurve ?? []}
        startEnhancer={glyphs.Milestone({})}
        placeholder={t("milestones.general.milestone", { defaultValue: "Milestone" })}
        onChange={(value) => {
          dispatch({
            milestone: {
              selectedMilestone: feedCurve?.milestones?.find(f => f.feedCurveMilestoneId === value?.id), selectedDay: undefined 
          } })
          if (milestoneDayRef.current) {
            milestoneDayRef.current.setValue("");
          }
        }}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greySecondary,
          fieldWidth: "180px"
        }}
        valueOverrides={{
          defaultValue: selectedMilestone ? milestoneOptionsFromFeedcurve?.find(f => f.id === selectedMilestone?.feedCurveMilestoneId) : undefined,
        }}
      />

      <NewInput
        ref={milestoneDayRef}
        isClearable
        startEnhancer={glyphs.MilestoneDay({})}
        placeholder={t("milestones.general.milestoneDay", {defaultValue: "Milestone Day"})}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greySecondary,
          fieldWidth: "180px"
        }}
        valueOverrides={{
          valueType: "number",
          minValueNumber: 0,
          maxValueNumber: maxDay
        }}
        onChange={(value) => { selectedMilestone !== undefined && dispatch({milestone: {selectedMilestone: selectedMilestone, selectedDay: Number(value)}})}}
        onKeyEvents={{
            onKeyDown: (e) => {
              if (selectedMilestone === undefined || selectedDay === undefined) return;
              if (e.key === "ArrowUp") {
                e.preventDefault();
                changeDay("up");
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                changeDay("down");
              }
            }
          }}
        isDisabled={selectedMilestone === undefined}
      />
      {selectedMilestone !== undefined && selectedDay !== undefined ?
        <NewButton
          content={t("reset", {defaultValue: "reset"})}
          buttonType={ButtonTypes.textButton}
          onClick={() => { 
            dispatch({milestone: {selectedMilestone: undefined, selectedDay: undefined}})
          }}
        />
      : <></>}
    </div>
  )
  // #endregion

  // #region | Unit Btns
  const UnitBtns = ( <NewButtonList 
    contents={[ t("units.kg", { defaultValue: "KG" }), t("units.pct", { defaultValue: "PCT" }), t("buttons.energy", { defaultValue: "Energy" }) ]} 
    onClick={(i => {
      if (i === 0) { setUnitMode(Unit.kg) }
      if (i === 1) { setUnitMode(Unit.percentile) }
      if (i === 2) { setUnitMode(Unit.energy) }
    })}
    overrides={(index) => ({
      baseStyles: { borderRadius: "8px", padding: "5px", backgroundColor: (unitMode === Unit.kg&&index === 0) || (unitMode === Unit.percentile&&index === 1) || (unitMode === Unit.energy&&index === 2) ? colors.greyDarkSecondary : colors.transparent },
      hoverStyles: { backgroundColor: colors.greyDarkSecondary }
    })}
    buttonType={ButtonTypes.tableButton}
    startEnhancers={[ glyphs.Kg({}), glyphs.Percent({}), glyphs.Energy({})]}
    listOverrides={{
      listGap: "10px"
    }}
    popOverrides={{
      hasPop: true,
      popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "10px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d", right: "-10px", top: "55px", left: "unset"},
      buttonType: ButtonTypes.tableButton,
      openContent: "Unit",
      closeContent: "Unit",
      closeEnhancer: <div style={{}}>{glyphs.Close({})}</div>,
      openEnhancer: unitMode === Unit.kg ? <>{glyphs.Kg({})}</> : unitMode === Unit.percentile ? <>{glyphs.Percent({})}</> : <>{glyphs.Energy({})}</>
    }}
  />)
  // #endregion

  // #region | Default Btns
  const defaultBtns = ( <NewButtonList
    contents={editTable ? [ t("buttons.save", {defaultValue: "Save"}), t("buttons.cancel", { defaultValue: "Cancel" }), t("buttons.edit", { defaultValue: "Edit" }), ] : [t("buttons.edit", { defaultValue: "Edit" })]} 
      onClick={(i) => {
        if (editTable) {
          if (i === 0) { saveChanges(); switchTable() }
          else if (i === 1) { switchTable(); clearDirt() }
          else if (i === 2) { }
        } else {
          if (i === 0) { switchTable() }
        }
      }}
    disabled={(i) => editTable && i === 2 ? true : false}
    buttonType={ButtonTypes.tableButton}
    startEnhancers={editTable ? [ glyphs.Check({}), glyphs.Close({}), glyphs.Edit({}) ] : [glyphs.Edit({})]}
    overrides={(i) => {
      return {
        baseStyles: { backgroundColor: editTable && i === 2 ? colors.greyPrimary : colors.greySecondary } 
      }
    }}
  /> )
  // #endregion
  let minRows = 16;
  if (isScreenHeightMax) {
    minRows = 17;
  }
  if (mobileXs) {
    minRows=28;
  }
  return (
      <div style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: "50px 1fr", gridTemplateColumns: "35px 1fr", boxSizing: "border-box" }}>
        {editTable ? createPortal(<Overlay overrides={{ zIndex: editTable ? 99 : 0 }} beforeUnload onClickAlert />, document.body) : <></>}
        
        <div style={{ gridArea: "1/1/2/3", backgroundColor: "transparent", display: "flex", justifyContent: "end", alignItems: "end", padding: "0px 20px" }}>
          {milestoneSelect}
          <div style={{ zIndex: 9999999, display: "flex", justifyContent: "center", alignItems: "center", gap: "5px" }}>
            {defaultBtns}
            {UnitBtns}
          </div>
        </div>
      
        {/* FeedCurveTable */}
        <div style={{gridArea: "2/1/3/3", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid "+colors.greyPrimary, borderRadius: "10px", overflow: "hidden", zIndex: editTable ? "999999" : "0"}}>
          <ReactiveTable minRows={minRows} loading={loading} highlightedRowId={highlightedRowId} key={`FeedCurveTable-${unitMode}`} table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("feed.feedCurve.noFeedCurve", {defaultValue: "no feedCurve found"})} tableToggled={Number(editTable)-(Number(unitMode)+(feedCurve.components?.length??0))} />
        </div>
      
        <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", gridArea: "2/1/3/2" }}>
        {(isScreenHeightMax || mobileXs) ? 
          <TableButton
            content={t("feed.feedCurve.feedCurve", {defaultValue: "feed curve"})}
            disabled={editTable ? true : false}
            onClick={() => { if (watchFeedCurve) watchFeedCurve() }}
            startEnhancer={glyphs.FeedCurve({})}
          /> : <></>
        }
        </div>

      
      {Boolean(errorMilestones.length) && createPortal(
        <PortalWrapper onAnimationEnd={() => {setErrorMilestones([]); setErrorMessage(undefined)}} style={{zIndex: errorMilestones.length ? "999999" : "0"}}>
          <span style={{color: colors.greyDarkPrimary, fontWeight: 'bold', display: "flex", justifyContent: "center", alignContent: "center"}}>{`Something went wrong`.toUpperCase()}</span>

          <div style={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
            <div style={{width: "100%", height: "2px", backgroundColor: colors.greyDarkPrimary}}/>
          </div>

          <span style={{color: colors.greyDarkPrimary, display: "flex", justifyContent: "center", alignContent: "center"}}>{errorMessage ? errorMessage : `When attempting to maintain feed curve milestone: ${errorMilestones.join(", ")}`}</span>              
        </PortalWrapper>, document.body)
      }
      
    </div>
  )
}

export default FeedCurveTable

// #region | Component Popover
const ThisComponent = memo(({ feedComponent, dbComponent }: { feedComponent?: FeedCurveComponent, dbComponent?: FeedComponentInfo }) => {
  const { t } = useTranslation();
  if (!feedComponent || !dbComponent) return null;
  
  const fallbackTexts = {
    ds: t("negativeParams.ds", { defaultValue: "No DS" }),
    energy: t("negativeParams.energy", { defaultValue: "No energy" }),
    limit: t("negativeParams.limit", { defaultValue: "No limit" }),
    gramPerMinute: t("negativeParams.gramPerMinute", { defaultValue: "No gram per minute" }),
    priority: t("negativeParams.priority", { defaultValue: "No priority" }),
    protein: t("negativeParams.protein", { defaultValue: "No protein" }),
  };

  const displayData:Record<string, { value: string | number | JSX.Element, unit?: string }> = {
    name: { value: feedComponent.name },
    protein: { value: dbComponent.protein, unit: "%" },
    energy: {value: feedComponent.energy, unit: t("units.energy", { defaultValue: "energy" })},
    ds: { value: feedComponent.ds, unit: "%"},
    gramPerMinute: {value: feedComponent.gramPerMinute, unit: t("units.gramPerMinute", { defaultValue: "g/min" })},
    limit: { value: feedComponent.limit },
    priority: {value: dbComponent.priority},
    readyMixture: { value: dbComponent.readyMixture === false ? <glyphs.Minus size='15px' fillColor={colors.systemRed} /> : <glyphs.Check size='15px' fillColor={colors.systemGreen} />}
  }

  return (
    <div>
      {Object.entries(displayData).map(([label, value]) => (
        <div key={label} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px"}}>
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2,  display: "flex", alignItems: "center", justifyContent: "right", height: "20px" }}> {(t(`feedCurvePages.components.${label}`))} </p>
          <p style={{ opacity: value.value === undefined ? "0.5" : "1", fontStyle: value.value === undefined ? "italic" : "none", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, height: "20px", display: "flex", alignItems: "center", justifyContent: "left" }}>
            {value.value !== undefined ? (
              <>
                {value.value}
                {value.unit && typeof value.value !== "object" && ` ${value.unit}`}
              </>
            ) : (
              label in fallbackTexts ? fallbackTexts[label as keyof typeof fallbackTexts] : ""
            )}  
          </p>
        </div>
      ))} 
    </div>
  )
})

const FADE_OUT_TIME = 4000; // ms
const PortalWrapper = styled(`div`, {
  opacity: "0",
  animation: `fadeInStayOut ${FADE_OUT_TIME}ms ease-in-out forwards`,
  display:"flex",
  flexDirection:"column",
  justifyContent: "center",
  alignContent: "center",
  position: "absolute",
  bottom: "50%",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: colors.systemYellow,
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d",
  "@keyframes fadeInStayOut": {
    "0%": {
        opacity: "0"
    },
    "10%": {
        opacity: "1"
    },
    "90%": {
        opacity: "1"
    },
    "100%": {
        opacity: "0"
    }
  } 
})

// #endregion